import type { ApolloError, FetchResult } from '@apollo/client';
import * as Sentry from '@sentry/react';
import {
  useGetMyProfileQuery,
  useUsBoGetTokenStatusQuery,
  useLogoutMutation,
  useClearSessionMutation,
  type LogoutMutation,
  type LogoutMutationOptions,
  type UsUser,
  useLoginTwoFactorMutation,
  type LoginTwoFactorMutation,
  type LoginTwoFactorMutationOptions,
} from '@shared/graphql';
import { createContext, useMemo, useState, useEffect, useCallback } from 'react';

import { mapUserProfile, type UserProfile, type SessionInfo } from './mapper';

export enum AuthenticationState {
  LOADING = 'LOADING',
  LOGGED_IN = 'LOGGED_IN',
  NOT_LOGGED_IN = 'NOT_LOGGED_IN',
  PENDING_2FA = 'PENDING_2FA',
  SUSPENDED_2FA = 'SUSPENDED_2FA',
}

export interface Authentication {
  authenticationState: AuthenticationState;
  userProfile: UserProfile | null;
  sessionInfo: SessionInfo;
  logout: (option?: LogoutMutationOptions) => Promise<FetchResult<LogoutMutation>>;
  login: (option: LoginTwoFactorMutationOptions) => Promise<FetchResult<LoginTwoFactorMutation>>;
  profileLoading: boolean;
  isScopeLoading: boolean;
  forceLogout: () => Promise<void>;
}

const AuthenticationContext = createContext<Authentication | undefined>(undefined);
let externalForceLogout: (() => void) | null = null;
export const getExternalForceLogout = () => externalForceLogout;
export const setExternalForceLogout = (fn: (() => void) | null) => {
  externalForceLogout = fn;
};

interface AuthenticationProviderProps {
  children: React.ReactNode;
}

function AuthenticationProvider(props: Readonly<AuthenticationProviderProps>) {
  const { children } = props;

  const [authenticationState, setAuthenticationState] = useState(AuthenticationState.LOADING);

  const {
    loading: isScopeLoading,
    error: scopeError,
    data: tokenStatusInfo,
    refetch: refetchTokenStatus,
  } = useUsBoGetTokenStatusQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.UsBoGetTokenStatus.result.twoFactorStatus === 'LOCKED') {
        setAuthenticationState(AuthenticationState.SUSPENDED_2FA);
      } else if (data.UsBoGetTokenStatus.result.scopes?.includes('two-factor-auth')) {
        setAuthenticationState(AuthenticationState.PENDING_2FA);
      }
    },
    onError: () => {
      setAuthenticationState(AuthenticationState.NOT_LOGGED_IN);
    },
  });

  const isPending2FA =
    tokenStatusInfo?.UsBoGetTokenStatus.result.twoFactorStatus === 'LOCKED' ||
    tokenStatusInfo?.UsBoGetTokenStatus.result.scopes?.includes('two-factor-auth');

  const {
    data,
    loading: isProfileLoading,
    client,
  } = useGetMyProfileQuery({
    skip: isScopeLoading || !!scopeError || isPending2FA,
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      if (!data.getMyProfile) {
        setAuthenticationState(AuthenticationState.NOT_LOGGED_IN);
      } else {
        setAuthenticationState(AuthenticationState.LOGGED_IN);
      }
    },
    onError: () => {
      setAuthenticationState(AuthenticationState.NOT_LOGGED_IN);
    },
  });

  const myProfile = data?.getMyProfile as UsUser;
  const userProfile = useMemo(() => (myProfile ? mapUserProfile(myProfile) : null), [myProfile]);

  const sessionInfo = useMemo(
    () => ({
      ttl: tokenStatusInfo?.UsBoGetTokenStatus.result.ttl ?? 0,
      scopes: tokenStatusInfo?.UsBoGetTokenStatus.result.scopes ?? [],
      twoFactorStatus: tokenStatusInfo?.UsBoGetTokenStatus.result.twoFactorStatus ?? 'unknown',
      email: tokenStatusInfo?.UsBoGetTokenStatus.result.email ?? '',
    }),
    [tokenStatusInfo],
  );

  const [loginTwoFactor] = useLoginTwoFactorMutation({
    onError: (error: ApolloError) => console.error(error),
    refetchQueries: [],
    onCompleted: async (data) => {
      if (data?.suLoginTwoFactor?.success) {
        await refetchTokenStatus();
      }
    },
    awaitRefetchQueries: true,
  });

  const [clearSession] = useClearSessionMutation();

  const resetClientState = useCallback(async () => {
    await client.resetStore();
    client.cache.modify({
      fields: {
        getMyProfile: () => null,
      },
    });
    setAuthenticationState(AuthenticationState.NOT_LOGGED_IN);
  }, [client]);

  const [logout] = useLogoutMutation({
    onCompleted: async () => {
      await resetClientState();
    },
    onError: async () => {
      try {
        await clearSession();
      } catch {
        // ignore — best-effort cookie clear
      }
      await resetClientState();
    },
  });

  const forceLogout = useCallback(async () => {
    try {
      // clearStore keeps existing cache structure but empties data; call resetStore() later if you need active queries to run again
      await client.clearStore?.();
    } catch {
      // ignore
    }
    client.cache.modify({
      fields: {
        getMyProfile: () => null,
      },
    });
    setAuthenticationState(AuthenticationState.NOT_LOGGED_IN);
  }, [client]);

  useEffect(() => {
    setExternalForceLogout(forceLogout);
    return () => setExternalForceLogout(null);
  }, [forceLogout]);

  useEffect(() => {
    if (!Sentry.isInitialized()) return;

    if (userProfile) {
      Sentry.setUser({ id: userProfile.id, email: userProfile.email });
      Sentry.setContext('User Profile', {
        id: userProfile.id,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        displayName: userProfile.displayName,
        email: userProfile.email,
        appRoles: userProfile.appRoles,
        status: userProfile.status,
        isAdmin: userProfile.isAdmin,
      });
    } else {
      Sentry.setUser(null);
      Sentry.setContext('User Profile', null);
    }
  }, [userProfile]);

  const contextValue = useMemo(() => {
    return {
      authenticationState,
      userProfile,
      sessionInfo,
      logout: logout,
      login: loginTwoFactor,
      profileLoading: isProfileLoading || isScopeLoading,
      isScopeLoading: isScopeLoading,
      forceLogout,
    };
  }, [
    authenticationState,
    userProfile,
    sessionInfo,
    logout,
    loginTwoFactor,
    isProfileLoading,
    isScopeLoading,
    forceLogout,
  ]);
  return (
    <AuthenticationContext.Provider value={contextValue}>{children}</AuthenticationContext.Provider>
  );
}

export { AuthenticationContext, AuthenticationProvider };
